import { Menu } from 'antd';
import React, { useState } from 'react';
import { useTheme, withTheme } from '../ThemeProvider';
import { StyledSidebar } from './styles';
import { SidebarItem, SidebarMenuGroup, SidebarProps } from './types';

function isMenuGroup(item: SidebarItem): item is SidebarMenuGroup {
  return (item as SidebarMenuGroup).type === 'group';
}

function buildMenuItems(items: SidebarItem[], theme: any): any[] {
  return items.map(item => {
    if (isMenuGroup(item)) {
      // Group
      return {
        type: 'group',
        label: (
          <span
            className="text-xs font-normal"
            style={{ color: theme?.colors.primary.main.linear }}
          >
            {item.label}
          </span>
        ),
        key: `group-${item.label}`,
        children: buildMenuItems(item.children, theme),
      };
    } else {
      // Single item
      return {
        key: item.key,
        icon: item.icon,
        label: item.label,
        children: item.children ? buildMenuItems(item.children, theme) : undefined,
      };
    }
  });
}

const SidebarComponent: React.FC<SidebarProps> = ({
  logo,
  items = [],
  onSelect,
  selectedKey,
  selectedKeys,
  collapsed: controlledCollapsed,
  collapsedWidth = 80,
  collapsible = true,
  breakpoint = 'lg',
  theme: siderTheme = 'light',
  width = 256,
  trigger,
  onBreakpoint,
  onCollapse,
  className,
  style,
  showTrigger = true,
  reverseArrow = false,
}) => {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const isControlled = controlledCollapsed !== undefined;
  const collapsed = isControlled ? controlledCollapsed : internalCollapsed;
  const { theme } = useTheme();

  const handleCollapse = (newCollapsed: boolean, type: 'clickTrigger' | 'responsive') => {
    if (!isControlled) {
      setInternalCollapsed(newCollapsed);
    }
    onCollapse?.(newCollapsed, type);
  };

  const handleSelect = ({ key, keyPath }: { key: string; keyPath: string[] }) => {
    onSelect?.(key, keyPath);
  };

  const menuItems = buildMenuItems(items, theme);

  return (
    <StyledSidebar
      className={className}
      style={style}
      collapsed={collapsed}
      collapsedWidth={collapsedWidth}
      collapsible={collapsible}
      breakpoint={breakpoint}
      theme={siderTheme}
      width={width}
      trigger={showTrigger ? trigger : null}
      onBreakpoint={onBreakpoint}
      onCollapse={handleCollapse}
      reverseArrow={reverseArrow}
      originalTheme={theme}
    >
      {logo && (
        <div className="sidebar-logo">
          {typeof logo === 'string' ? (
            <img src={logo} alt="logo" className="h-auto w-auto max-w-full max-h-7 object-cover" />
          ) : (
            logo
          )}
        </div>
      )}
      <Menu
        mode="inline"
        items={menuItems}
        selectedKeys={
          selectedKeys && selectedKeys.length ? selectedKeys : selectedKey ? [selectedKey] : []
        }
        openKeys={openKeys}
        onOpenChange={setOpenKeys}
        onSelect={handleSelect}
        style={{ borderRight: 'none', background: 'transparent' }}
      />
    </StyledSidebar>
  );
};

export const Sidebar = withTheme(SidebarComponent);
